import { useT } from '@/lib/i18n';
import { Sparkles, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export const ConceptCard = ({ concept, accentColor = '#0A0E1A', dataTestId, showPrototypeCta = false }) => {
  const t = useT();
  if (!concept) return null;
  const modules = Array.isArray(concept.modules) ? concept.modules : [];
  const nocode = Array.isArray(concept.nocode_blocks) ? concept.nocode_blocks : [];

  return (
    <article
      data-testid={dataTestId || `concept-card-${concept.id}`}
      className="relative flex flex-col overflow-hidden rounded-3xl border border-[var(--om-border)] bg-white p-7 shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_20px_40px_-30px_rgba(10,14,26,0.18)]"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-16 -right-16 h-56 w-56 rounded-full opacity-70 blur-3xl"
        style={{ background: `radial-gradient(closest-side, ${accentColor}22, transparent 70%)` }}
      />
      <div className="relative flex items-center gap-2">
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider"
          style={{ color: accentColor, background: `${accentColor}18` }}
        >
          <Sparkles size={12} strokeWidth={2.6} /> {t('generator.samples_eyebrow')}
        </span>
        <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
          ⏱ {concept.estimated_build_time}
        </span>
      </div>

      <h3 className="relative mt-5 text-[22px] font-bold tracking-tight text-[var(--om-ink)]">
        {concept.app_name}
      </h3>
      <p className="relative mt-2 text-sm leading-relaxed text-[var(--om-muted)]">
        {concept.pitch}
      </p>

      {modules.length > 0 && (
        <div className="relative mt-5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
            {t('generator.section_modules', { count: modules.length })}
          </div>
          <ul className="mt-2 space-y-1.5">
            {modules.slice(0, 3).map((m, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[var(--om-ink-soft)]">
                <span
                  className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                  style={{ background: accentColor }}
                />
                <span>
                  <span className="font-semibold text-[var(--om-ink)]">{m.name}</span>
                  {m.ai_powered && (
                    <span
                      className="ml-2 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                      style={{ color: accentColor, background: `${accentColor}18` }}
                    >
                      {concept.lang === 'en' ? 'AI' : 'IA'}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {nocode.length > 0 && (
        <div className="relative mt-5 flex flex-wrap gap-1.5">
          {nocode.slice(0, 5).map((b, i) => (
            <span
              key={i}
              className="rounded-full border border-[var(--om-border)] bg-[var(--om-bg)] px-2.5 py-1 text-[11px] font-medium text-[var(--om-muted)]"
            >
              {b}
            </span>
          ))}
          {nocode.length > 5 && (
            <span className="rounded-full border border-[var(--om-border)] bg-white px-2.5 py-1 text-[11px] font-semibold text-[var(--om-ink)]">
              +{nocode.length - 5}
            </span>
          )}
        </div>
      )}

      {showPrototypeCta && concept.id && (
        <div className="relative mt-6 pt-5 border-t border-[var(--om-border)]">
          <Link
            to={`/prototype/${concept.id}`}
            className="inline-flex items-center gap-1.5 text-[13px] font-bold transition-colors"
            style={{ color: accentColor }}
            data-testid={`concept-card-${concept.id}-prototype-cta`}
          >
            {t('prototype.build_cta_view')}
            <ArrowRight size={13} strokeWidth={2.4} />
          </Link>
        </div>
      )}
    </article>
  );
};
